<script setup lang="ts">
  import FloatingAiButton from '@/components/app/chat/sidepanel/FloatingAiButton.vue'
  import MergedChatsReleaseOverlay from '@/components/app/chat/sidepanel/MergedChatsReleaseOverlay.vue'
  import SidePanelChat from '@/components/app/chat/sidepanel/SidePanelChat.vue'
  import { useFeatureFlag } from '@/composables/useFeatureFlag'
  import { useUserStore } from '@/shared/stores/userStore'
  import { useUnifiedChatStore } from '@/store/app/unifiedChat'
  import { ROUTES } from '@/router/routes'
  import { caseUuidFromAppCaseDetailRoute } from '@/utils/caseDetailRoute'
  import { FlagEnum } from '@/types/generated'
  import { storeToRefs } from 'pinia'
  import { computed, onMounted, watch } from 'vue'
  import { useRoute } from 'vue-router'

  const route = useRoute()
  const userStore = useUserStore()
  const unifiedChat = useUnifiedChatStore()
  const { isSidePanelOpen } = storeToRefs(unifiedChat)
  const floatingAiButtonFlag = useFeatureFlag(FlagEnum.FLOATING_AI_BUTTON)

  const globalChatPathPrefix = ROUTES.APP.GLOBAL_CHAT.ROOT
  const aiAssistantPathPrefix = ROUTES.APP.AI_ASSISTANT.ROOT

  const isPublicMatched = computed(() => route.matched.some((r) => r.meta?.public === true))

  /**
   * Whether floating AI chrome is allowed for this path (ignores org / PostHog).
   */
  function floatingRouteEligibleForPath(path: string, publicMatched: boolean): boolean {
    if (!path.startsWith('/app')) {
      return false
    }
    if (publicMatched) {
      return false
    }
    if (path === '/app/start') {
      return false
    }
    if (path.startsWith(globalChatPathPrefix) || path.startsWith(aiAssistantPathPrefix)) {
      return false
    }
    return true
  }

  /**
   * Path-level eligibility for floating AI chrome (no org / feature-flag checks).
   * Used so we keep the side panel mounted when it is open even if the PostHog flag flickers.
   */
  const floatingAiRouteEligible = computed(() =>
    floatingRouteEligibleForPath(route.path, isPublicMatched.value)
  )

  /** Route + org gates (PostHog flag checked separately in ``showFloatingAi``). */
  const routeAllowsFloatingAi = computed(() => {
    if (!floatingAiRouteEligible.value) return false
    const org = userStore.user?.org as { enable_law_specific_data?: boolean } | undefined
    if (org && org.enable_law_specific_data === false) return false
    return true
  })

  const showFloatingAi = computed(
    () => routeAllowsFloatingAi.value && floatingAiButtonFlag.isEnabled.value
  )

  /**
   * Mount chat chrome when the PostHog flag allows the entrypoint, or when the panel is already
   * open (flag flicker / async check must not tear down an open panel). Gated by
   * ``floatingAiRouteEligible`` so we never mount off the main ``/app`` shell (e.g. client portal).
   */
  const showChatShell = computed(
    () => floatingAiRouteEligible.value && (showFloatingAi.value || isSidePanelOpen.value)
  )

  watch(
    routeAllowsFloatingAi,
    async (allows) => {
      if (allows) {
        await floatingAiButtonFlag.checkFlag()
      }
    },
    { immediate: true }
  )

  onMounted(async () => {
    if (!userStore.user && routeAllowsFloatingAi.value) {
      try {
        await userStore.fetchUser()
      } catch {
        /* auth layer may redirect; avoid noisy errors */
      }
    }
  })

  /**
   * Close the panel only when entering a forbidden zone from an eligible route, or on first
   * evaluation if the **current** route is already forbidden. Never close on first run when the
   * route is eligible — otherwise reload on ``/app/...`` wipes ``sessionStorage`` and Pinia state.
   */
  watch(
    () => [route.path, isPublicMatched.value, route.matched.length] as const,
    ([path, publicMatched, matchedLen], old) => {
      const forbidden = !floatingRouteEligibleForPath(path, publicMatched)

      if (!forbidden) {
        return
      }

      if (old === undefined) {
        // Avoid closing while the router has not finished matching (can look like a non-/app path).
        if (matchedLen === 0) {
          return
        }
        unifiedChat.closeSidePanel()
        return
      }

      const [prevPath, prevPublic] = old
      if (floatingRouteEligibleForPath(prevPath, prevPublic)) {
        unifiedChat.closeSidePanel()
      }
    },
    { immediate: true }
  )

  /**
   * Sync the side panel's case context with the current route. Matches `/app/cases/:uuid`
   * (any sub-route). When navigating away from a case, the context is cleared so the panel
   * falls back to a general/global chat.
   */
  const caseUuidFromRoute = computed<string | null>(() => caseUuidFromAppCaseDetailRoute(route))

  watch(
    caseUuidFromRoute,
    (uuid) => {
      if (!routeAllowsFloatingAi.value) return
      if (uuid) {
        void unifiedChat.setContextCase(uuid)
      } else {
        unifiedChat.clearContextCase()
      }
    },
    { immediate: true }
  )
</script>

<template>
  <template v-if="showChatShell">
    <FloatingAiButton v-if="showFloatingAi && !isSidePanelOpen" />
    <SidePanelChat />
    <MergedChatsReleaseOverlay v-if="showFloatingAi && !isSidePanelOpen" />
  </template>
</template>
